import { Link, useParams } from 'react-router-dom';

import type { SessionListRow } from '../../games/playtest-sessions-data.js';
import { formatMarkerBuild } from './format-build-marker.js';
import { StageBadge } from './stage-badge.js';
import { SessionStateBadge } from './state-badge.js';

interface Props {
  row: SessionListRow;
  playtestId: string;
}

export function SessionRow({ row, playtestId }: Props): JSX.Element {
  const params = useParams<{ studioShortId: string; gameShortId: string }>();
  const tester =
    row.testerDisplayName ?? `tester ${row.session.discordUserId.slice(-4)}`;
  const href = `/s/${params.studioShortId}/g/${params.gameShortId}/playtests/${playtestId}/sessions/${row.session.id}`;

  return (
    <Link to={href} className="block text-sm transition hover:bg-surface-2">
      <div className="flex flex-col gap-1.5 px-4 py-3 md:hidden">
        <div className="flex items-start justify-between gap-2">
          <span className="min-w-0 truncate font-medium text-text">{tester}</span>
          <SessionStateBadge state={row.session.state} />
        </div>
        <div className="flex min-w-0 items-center gap-1.5 text-xs text-text-muted">
          {row.buildMarker !== null ? (
            <>
              <span className="truncate">{formatMarkerBuild(row.buildMarker)}</span>
              {row.buildMarker.stage !== null ? <StageBadge stage={row.buildMarker.stage} /> : null}
            </>
          ) : (
            <span>Unregistered build</span>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 text-xs text-text-muted">
          <span>Started {formatRelative(row.session.startedAt)}</span>
          <span>Active {formatRelative(row.session.lastActivityAt)}</span>
        </div>
      </div>

      <div className="hidden grid-cols-12 items-center gap-2 px-3 py-2 md:grid">
        <span className="col-span-4 min-w-0 truncate text-text">{tester}</span>
        <span className="col-span-2 flex min-w-0 items-center gap-1.5 text-text-muted">
          {row.buildMarker !== null ? (
            <>
              <span className="truncate">{formatMarkerBuild(row.buildMarker)}</span>
              {row.buildMarker.stage !== null ? <StageBadge stage={row.buildMarker.stage} /> : null}
            </>
          ) : (
            'Unregistered'
          )}
        </span>
        <span className="col-span-2">
          <SessionStateBadge state={row.session.state} />
        </span>
        <span className="col-span-2 text-xs text-text-muted">
          {formatRelative(row.session.startedAt)}
        </span>
        <span className="col-span-2 text-xs text-text-muted">
          {formatRelative(row.session.lastActivityAt)}
        </span>
      </div>
    </Link>
  );
}

function formatRelative(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return iso;
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  return new Date(then).toLocaleDateString();
}
